/**
 * api-fetch — Helper client-side qui injecte automatiquement le JWT Supabase.
 *
 * Remplace les `fetch('/api/...', {...})` directs dans les hooks/composants.
 * Utilisé par postJsonWithTimeout (fetch-utils.ts) pour propager le JWT à tous
 * les appels /api/ai sans modifier chaque hook individuellement.
 */

import { createClient } from './supabase/client';

/**
 * Erreur levée quand le serveur retourne 402 (quota mensuel épuisé).
 * Les hooks AI l'interceptent pour afficher le FreemiumGate.
 */
export class InsufficientCreditsError extends Error {
  constructor(
    message: string,
    public readonly remaining: number = 0,
    public readonly plan: string = 'free',
  ) {
    super(message);
    this.name = 'InsufficientCreditsError';
  }
}

/**
 * Récupère le token d'accès de la session courante Supabase.
 * Retourne null si pas de session (utilisateur anonyme).
 */
export async function getAccessToken(): Promise<string | null> {
  try {
    const supabase = createClient();
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  } catch {
    return null;
  }
}

/**
 * POST JSON vers un endpoint API avec JWT auto-injecté.
 * Lance InsufficientCreditsError sur réponse 402.
 */
export async function apiFetch<T = unknown>(
  url: string,
  body: unknown,
  opts: {
    signal?: AbortSignal;
    extraHeaders?: Record<string, string>;
    method?: 'POST' | 'PUT' | 'PATCH';
  } = {},
): Promise<{ res: Response; json: T }> {
  const token = await getAccessToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...opts.extraHeaders,
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    method: opts.method ?? 'POST',
    credentials: 'same-origin',
    headers,
    body: JSON.stringify(body),
    signal: opts.signal,
  });

  // Gérer 402 avant de parser le JSON
  if (res.status === 402) {
    let errData: { error?: string; remaining?: number; plan?: string } = {};
    try { errData = await res.clone().json(); } catch { /* ignore */ }
    throw new InsufficientCreditsError(
      errData.error ?? 'Quota mensuel épuisé. Passez à un plan supérieur.',
      errData.remaining ?? 0,
      errData.plan ?? 'free',
    );
  }

  const text = await res.text();
  let json: T;
  try {
    json = JSON.parse(text) as T;
  } catch {
    throw new Error(text.slice(0, 500) || 'Réponse invalide du serveur');
  }

  return { res, json };
}

/**
 * DELETE vers un endpoint API avec JWT auto-injecté.
 */
export async function apiFetchDelete(
  url: string,
): Promise<void> {
  const token = await getAccessToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  await fetch(url, { method: 'DELETE', credentials: 'same-origin', headers });
}

/**
 * GET vers un endpoint API avec JWT auto-injecté.
 */
export async function apiFetchGet<T = unknown>(
  url: string,
  opts: { extraHeaders?: Record<string, string> } = {},
): Promise<{ res: Response; json: T }> {
  const token = await getAccessToken();

  const headers: Record<string, string> = { ...opts.extraHeaders };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(url, { method: 'GET', credentials: 'same-origin', headers });

  const text = await res.text();
  let json: T;
  try {
    json = JSON.parse(text) as T;
  } catch {
    throw new Error(text.slice(0, 500) || 'Réponse invalide du serveur');
  }

  return { res, json };
}
